import type { Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { createContext, useMemo, useState } from 'react';
import { useContextOrThrow } from '@elfsight/lib-utils';

type CheckoutStep = 'form' | 'payment' | 'success';

type CheckoutNavigation = {
  withForm: boolean;

  checkoutStep: CheckoutStep;
  setCheckoutStep: Dispatch<SetStateAction<CheckoutStep>>;
  resetCheckoutStep: () => void;

  isCheckoutOpen: boolean;
  closeCheckoutComponent: () => void;

  isSummaryOpen: boolean;
  openSummary: () => void;
  closeSummary: () => void;
};

const CheckoutNavigationContext = createContext<CheckoutNavigation | null>(
  null
);

type CheckoutNavigationProviderProps = {
  withForm: boolean;
  isCheckoutOpen: boolean;
  closeCheckoutComponent: () => void;
};

export function CheckoutNavigationProvider({
  isCheckoutOpen,
  closeCheckoutComponent,
  children,
  withForm
}: PropsWithChildren<CheckoutNavigationProviderProps>) {
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>(
    withForm ? 'form' : 'payment'
  );
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);

  const navigation = useMemo(
    () => ({
      withForm,
      checkoutStep,
      setCheckoutStep,
      resetCheckoutStep: () => setCheckoutStep(withForm ? 'form' : 'payment'),

      isCheckoutOpen,
      closeCheckoutComponent,
      isSummaryOpen,

      openSummary: () => setIsSummaryOpen(true),
      closeSummary: () => setIsSummaryOpen(false)
    }),
    [
      withForm,
      checkoutStep,
      closeCheckoutComponent,
      isCheckoutOpen,
      isSummaryOpen
    ]
  );

  return (
    <CheckoutNavigationContext.Provider value={navigation}>
      {children}
    </CheckoutNavigationContext.Provider>
  );
}

export function useCheckoutNavigation() {
  return useContextOrThrow(CheckoutNavigationContext, 'CheckoutNavigation');
}
